import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ShieldAlert, Users, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ISSUE_LABELS = {
  NON_PAYMENT: 'Non-payment',
  DANGEROUS_ANIMAL: 'Dangerous animal',
  NO_SHOW: 'No-show',
  UNSAFE_CONDITIONS: 'Unsafe conditions',
  DIFFICULT_CLIENT: 'Difficult client',
  OTHER: 'Other',
};

/**
 * Banner that checks Provider Alerts for a given client (by name/email/phone)
 * and displays a warning if any active alerts exist. Pure read-only summary.
 */
export default function ClientAlertBanner({ clientName, clientEmail, clientPhone }) {
  const { token, user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // Only show for active providers
    if (!user || user.accountStatus !== 'ACTIVE') return;
    if (!clientName && !clientEmail && !clientPhone) return;

    const params = new URLSearchParams();
    if (clientName) params.set('name', clientName);
    if (clientEmail) params.set('email', clientEmail);
    if (clientPhone) params.set('phone', clientPhone);

    setLoading(true);
    axios
      .get(`${API_URL}/api/provider-alerts/check?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data?.hasAlerts) {
          setAlerts(res.data.alerts || []);
        } else {
          setAlerts([]);
        }
      })
      .catch(() => setAlerts([]))
      .finally(() => setLoading(false));
  }, [token, user, clientName, clientEmail, clientPhone]);

  if (loading || alerts.length === 0) return null;

  const hasMultiple = alerts.some((a) => a.credibilityLevel === 'MULTIPLE_REPORTS');

  return (
    <Alert
      className={hasMultiple ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-300'}
      data-testid="client-alert-banner"
    >
      <ShieldAlert className={hasMultiple ? 'h-4 w-4 text-red-600' : 'h-4 w-4 text-yellow-700'} />
      <AlertTitle className={hasMultiple ? 'text-red-900' : 'text-yellow-900'}>
        {alerts.length} Provider Alert{alerts.length > 1 ? 's' : ''} matched this client
      </AlertTitle>
      <AlertDescription className={hasMultiple ? 'text-red-800' : 'text-yellow-900'}>
        <div className="flex flex-wrap gap-2 mt-2">
          {alerts.slice(0, expanded ? alerts.length : 3).map((a) => (
            <Badge
              key={a.id}
              variant="outline"
              className={a.credibilityLevel === 'MULTIPLE_REPORTS' ? 'border-red-300' : 'border-yellow-300'}
            >
              {a.credibilityLevel === 'MULTIPLE_REPORTS' ? (
                <Users className="h-3 w-3 mr-1" />
              ) : (
                <ShieldAlert className="h-3 w-3 mr-1" />
              )}
              {a.targetClientName} • {(a.issueTypes || []).map((t) => ISSUE_LABELS[t] || t).join(', ')}
              {a.reportCount > 1 ? ` (${a.reportCount} reports)` : ''}
            </Badge>
          ))}
        </div>
        {alerts.length > 3 && (
          <Button
            variant="link"
            size="sm"
            className="mt-1 px-0 h-auto"
            onClick={() => setExpanded((e) => !e)}
          >
            {expanded ? (
              <>
                Show less <ChevronUp className="h-3 w-3 ml-1" />
              </>
            ) : (
              <>
                Show {alerts.length - 3} more <ChevronDown className="h-3 w-3 ml-1" />
              </>
            )}
          </Button>
        )}
        <p className="text-xs mt-2 opacity-80">
          Visit the Provider Lounge → Alerts tab for full details and to add your own report.
        </p>
      </AlertDescription>
    </Alert>
  );
}
